// src/utils/sourceIntake.js
//
// Pass 64 — SOURCE / CHANNEL INTAKE: the ONE normalized front door. Many source channels (email,
// WhatsApp, agent, hotel, partner API, public checkout, manual operator entry, future systems) enter
// Motus through ONE contract — a SourceIntakeEnvelope — NOT as channel-specific business objects.
//
//   EXTERNAL SOURCE → [source adapter] → SourceIntakeEnvelope → ClientIdentityEvidence (Pass 63)
//     → operator review → BookingCase (stage=inquiry) → RequestedSession[] → (Pass 62) canonical bookings
//
// This module is PURE (no Firestore, no React). It knows nothing about Gmail HTML, Google message ids,
// WhatsApp payloads, RoomBoss fields, hotel webhooks, or spreadsheet rows — all of that lives in the
// per-source ADAPTER, which hands this core an already-normalized shape. Raw source is preserved as
// evidence; the normalized DRAFT is a suggestion; only operator review establishes canonical truth.

import { normalizeClientEvidence } from './clientIdentity';

const str = (v) => String(v == null ? '' : v).trim();
const arr = (v) => (Array.isArray(v) ? v : []);

// ── intake state (an operational axis SEPARATE from BookingCase commercial stage) ───
// "What happened to this incoming source item?" — not "what is the commercial state of the enquiry?".
export const INTAKE_STATE = { NEW: 'new', NEEDS_REVIEW: 'needs_review', CONVERTED: 'converted', IGNORED: 'ignored', ERROR: 'error' };

// ── Pass 87 §10 — conservative intake CLASSIFICATION (a SUGGESTION, never authority) ───────────────
// Classifies an inbound source item so the operator's Intake Inbox can prioritise. It NEVER converts,
// confirms, or creates canonical truth; a false positive cannot create a booking (conversion is always an
// explicit operator action). UNKNOWN > WRONG: anything unclear → needs_review (reviewable), and a
// non_booking verdict requires POSITIVE non-booking signals AND no booking signal.
export const INTAKE_CLASS = {
  BOOKING_CANDIDATE: 'booking_candidate',
  BOOKING_UPDATE_CANDIDATE: 'booking_update_candidate',
  NON_BOOKING: 'non_booking',
  NEEDS_REVIEW: 'needs_review',
};

const BOOKING_SIGNAL_RE = /\b(book(ing|ed)?|lesson|lessons|instructor|guide|guiding|private|group|ski|snowboard|avail(able|ability)|enquir|inquir|reserv(e|ation)|half[\s-]?day|full[\s-]?day|beginner|intermediate|advanced)\b/i;
const UPDATE_SIGNAL_RE = /\b(cancel|cancellation|cancelled|reschedul|postpone|change (?:the|our|my|their) (?:booking|lesson|date|time)|move (?:the|our|my) (?:lesson|booking)|different date|amend)\b/i;
const NON_BOOKING_RE = /\b(unsubscribe|newsletter|out of office|auto[\s-]?reply|delivery (?:status|failure)|undeliverable|mailer-daemon|password reset|verify your email|invoice attached|receipt for your|view in browser|no[\s-]?reply)\b/i;

// classify from the subject + normalized text + whether a concrete requested date is present.
export function classifyIntake({ subject = '', text = '', hasConcreteDate = false } = {}) {
  const hay = `${toPlainText(subject)}\n${toPlainText(text)}`;
  const update = UPDATE_SIGNAL_RE.test(hay);
  const booking = BOOKING_SIGNAL_RE.test(hay) || hasConcreteDate;
  const nonBooking = NON_BOOKING_RE.test(hay);
  const reasons = [];
  if (update) reasons.push('change/cancellation language');
  if (booking) reasons.push(hasConcreteDate ? 'booking language + concrete date' : 'booking language');
  if (nonBooking) reasons.push('non-booking/automated-mail signal');
  let cls;
  if (update) cls = INTAKE_CLASS.BOOKING_UPDATE_CANDIDATE;           // a change/cancel always routes to the update lane
  else if (nonBooking && !booking) cls = INTAKE_CLASS.NON_BOOKING;    // clear non-booking AND no booking signal
  else if (booking) cls = INTAKE_CLASS.BOOKING_CANDIDATE;
  else cls = INTAKE_CLASS.NEEDS_REVIEW;                               // no signal either way → reviewable, never discarded
  return { classification: cls, reasons };
}

// ── Pass 87 §12 — operator-safe THREAD / UPDATE semantics (a SUGGESTION, never a mutation) ──────────
// Given a freshly-normalized intake + what is already known (ids already ingested, a case already linked to
// this thread), decide the operator action. It NEVER mutates a case/booking; a source change only SUGGESTS
// "change requested" — the operator/canonical workflow decides "change applied".
export const INTAKE_THREAD_ACTION = { NEW: 'new', REPLAY: 'replay', FOLLOW_UP: 'follow_up', UPDATE_CANDIDATE: 'update_candidate' };
export function resolveIntakeThreadAction({ intakeId = '', sourceConversationId = '', knownIntakeIds = [], threadLinkedCaseId = '', classification = '' } = {}) {
  const id = str(intakeId);
  if (id && arr(knownIntakeIds).map(str).includes(id)) {
    return { action: INTAKE_THREAD_ACTION.REPLAY, caseId: str(threadLinkedCaseId), reason: 'same source message id already ingested — idempotent replay, no new case' };
  }
  const caseId = str(threadLinkedCaseId);
  if (str(sourceConversationId) && caseId) {
    if (classification === INTAKE_CLASS.BOOKING_UPDATE_CANDIDATE) {
      return { action: INTAKE_THREAD_ACTION.UPDATE_CANDIDATE, caseId, reason: 'reply on a converted thread with change/cancellation language — operator reviews; canonical change is never auto-applied' };
    }
    return { action: INTAKE_THREAD_ACTION.FOLLOW_UP, caseId, reason: 'reply on a thread already linked to a case — attach as communication, no new case' };
  }
  return { action: INTAKE_THREAD_ACTION.NEW, caseId: '', reason: 'new source message on an unseen thread' };
}

// Business attribution (WHO/WHAT commercial channel) is separate from transport (HOW it arrived).
// Both are open strings; these are the known values, not a closed enum.
export const SOURCE_TYPE = { DIRECT: 'direct', AGENT: 'agent', HOTEL: 'hotel', PARTNER: 'partner', MANUAL: 'manual', PUBLIC: 'public_checkout' };
export const SOURCE_SYSTEM = { MANUAL: 'manual', EMAIL: 'email', WHATSAPP: 'whatsapp', PARTNER_API: 'partner_api', ROOMBOSS: 'roomboss', HOTEL_API: 'hotel_api', PUBLIC_CHECKOUT: 'public_checkout' };

// ── raw source content → safe plain text (untrusted input; NEVER rendered as HTML) ──
// Strips tags/scripts/entities to a conservative plain-text representation. The caller renders this as
// text only; the original raw stays in `rawSourceRef`/provenance as evidence, never as canonical truth.
export function toPlainText(rawInput = '') {
  let s = String(rawInput == null ? '' : rawInput);
  s = s.replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, ' '); // drop script/style bodies
  s = s.replace(/<[^>]+>/g, ' ');                                          // drop all remaining tags
  s = s.replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&quot;/gi, '"').replace(/&#39;/gi, "'");
  // eslint-disable-next-line no-control-regex -- deliberately stripping control chars from untrusted source
  s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, ' '); // strip control chars (keep newlines/tabs)
  s = s.replace(/[ \t]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim();
  return s;
}

const MAX_TEXT = 20000; // oversized-payload guard (keep evidence readable; reject the rest)
const clampText = (s) => (s.length > MAX_TEXT ? `${s.slice(0, MAX_TEXT)}\n…[truncated]` : s);

// ── deterministic intake identity (idempotency §12) ────────────────────────────
// org + sourceSystem + stable external record id. Customer email/phone/name are NEVER intake identity.
export function sourceIntakeKey({ organizationId = '', sourceSystem = '', sourceExternalId = '' } = {}) {
  const org = str(organizationId); const sys = str(sourceSystem); const ext = str(sourceExternalId);
  if (!org || !sys || !ext) return '';
  return `${org}::${sys}::${ext}`;
}
// The intake document id. A source with a stable external id → deterministic id (re-ingest = same doc).
// A source WITHOUT one (manual/paste) → the caller supplies a generated stable id at creation time.
export function intakeIdFromKey(key = '') { return key ? `intake_${key.replace(/[^A-Za-z0-9_-]/g, '_')}` : ''; }

// ── normalize any adapter output into the canonical envelope ─────────────────────
// `raw` is the adapter's already-source-neutral shape; `ctx` carries organizationId/seasonId/actor/now.
// Returns { intake, errors } — a fatal problem yields errors[] and state ERROR (fails safe, no throw).
export function normalizeSourceIntake(raw = {}, ctx = {}) {
  const errors = [];
  const organizationId = str(raw.organizationId || ctx.organizationId);
  if (!organizationId) errors.push('missing_organization');
  const sourceSystem = str(raw.sourceSystem) || SOURCE_SYSTEM.MANUAL;
  const sourceType = str(raw.sourceType) || SOURCE_TYPE.MANUAL;
  const sourceExternalId = str(raw.sourceExternalId);
  const key = sourceIntakeKey({ organizationId, sourceSystem, sourceExternalId });
  const intakeId = str(raw.intakeId) || intakeIdFromKey(key) || str(ctx.generatedIntakeId);
  if (!intakeId) errors.push('missing_intake_id'); // adapter without external id must be given a generated id

  const normalizedText = clampText(toPlainText(raw.normalizedText || raw.bodyText || raw.body || ''));
  const contact = raw.contact || {};
  const contactSnapshot = {
    name: str(contact.name || raw.requesterName),
    email: str(contact.email || raw.requesterEmail),
    phone: str(contact.phone || raw.requesterPhone),
  };
  // requested-session DRAFT lines (never canonical until operator review → case conversion)
  const requestedSessionsDraft = arr(raw.requestedSessionsDraft || raw.requestedSessions).map((l, i) => normalizeDraftLine(l, i));

  const intake = {
    schemaVersion: 1,
    organizationId,
    intakeId,
    seasonId: str(raw.seasonId || ctx.seasonId),
    // SOURCE ATTRIBUTION (business) vs SOURCE SYSTEM (transport) — kept distinct (§8)
    sourceType,
    sourceSystem,
    sourceExternalId,
    sourceConversationId: str(raw.sourceConversationId || raw.threadId),
    sourceCustomerId: str(raw.sourceCustomerId || raw.customerId),
    sourceDisplayLabel: str(raw.sourceDisplayLabel) || sourceSystem,
    receivedAt: raw.receivedAt || ctx.now || null,
    sourceOccurredAt: raw.sourceOccurredAt || null,
    subject: str(raw.subject).slice(0, 500),
    // RAW evidence: a safe reference/snapshot. We store plain-text; adapters may add a rawSourceRef.
    rawSourceRef: str(raw.rawSourceRef),
    normalizedText,
    // structured provenance (source-neutral; source-specific ids live here, not in the core case model)
    provenance: sanitizeProvenance(raw.provenance),
    // contact evidence + snapshot (feeds Pass-63 resolver; never itself the identity)
    contactSnapshot,
    requesterUserId: str(raw.requesterUserId),
    clientId: str(raw.clientId),
    // draft request
    requestedSessionsDraft,
    notes: clampText(toPlainText(raw.notes || '')),
    accommodationName: str(raw.accommodationName),
    accommodationAddress: str(raw.accommodationAddress),
    // one communication record for this inbound item (case.communications-compatible shape)
    communications: buildIntakeCommunications(raw, { sourceSystem, contactSnapshot }),
    // review + lifecycle
    reviewState: raw.reviewState || (requestedSessionsDraft.some((l) => l.needsReview) ? INTAKE_STATE.NEEDS_REVIEW : INTAKE_STATE.NEW),
    linkedBookingCaseId: str(raw.linkedBookingCaseId),
    errors,
  };
  // Pass 87 §10 — classification SUGGESTION (never authority). Computed from subject + text + whether any
  // concrete (non-review) requested date was extracted. Stored on the envelope for the Intake Inbox; it does
  // not gate conversion (operator remains authority) and a non_booking verdict never deletes/ignores anything.
  const hasConcreteDate = requestedSessionsDraft.some((l) => l.date && !l.needsReview);
  const cls = classifyIntake({ subject: intake.subject, text: normalizedText, hasConcreteDate });
  intake.classification = cls.classification;
  intake.classificationReasons = cls.reasons;
  if (errors.length) intake.reviewState = INTAKE_STATE.ERROR;
  return { intake, errors };
}

// A draft requested-session line. Conservative: only fields the source actually supported. Anything
// ambiguous (missing/unparseable date, unknown instructor count) is flagged needsReview — NEVER fabricated.
export function normalizeDraftLine(line = {}, idx = 0) {
  const date = str(line.date); // adapters pass an explicit ISO date or '' — we do not guess a year here
  const instructorsRequired = Number.isFinite(+line.instructorsRequired) && +line.instructorsRequired > 0 ? Math.floor(+line.instructorsRequired) : 1;
  const needsReview = !date || line.needsReview === true;
  return {
    lineId: str(line.lineId) || `draft-${idx + 1}`,
    date,
    startTime: str(line.startTime) || '09:00',
    bookingType: str(line.bookingType),
    discipline: str(line.discipline),
    skillLevel: str(line.skillLevel),
    instructorsRequired,
    groupSize: Number.isFinite(+line.groupSize) ? Math.floor(+line.groupSize) : null,
    location: str(line.location),
    notes: str(line.notes),
    needsReview,
    sourceHint: str(line.sourceHint), // the raw text fragment this line was drafted from (evidence)
    // Accelerator B — source-neutral external SESSION lineage (any external source's per-session id). Evidence,
    // NEVER business identity. Survives into the request line + canonical booking so booking.changed/cancelled can
    // deterministically resolve the exact external session without name/date matching.
    sourceExternalSessionId: str(line.sourceExternalSessionId),
  };
}

function sanitizeProvenance(p = {}) {
  const out = {};
  for (const [k, v] of Object.entries(p || {})) {
    if (v == null) continue;
    if (typeof v === 'object') continue; // provenance is flat scalars only (no nested/oversized payloads)
    out[str(k)] = typeof v === 'string' ? str(v).slice(0, 1000) : v;
  }
  return out;
}

function buildIntakeCommunications(raw, { sourceSystem, contactSnapshot }) {
  if (Array.isArray(raw.communications) && raw.communications.length) return raw.communications;
  const channel = sourceSystem === SOURCE_SYSTEM.EMAIL ? 'email' : (sourceSystem === SOURCE_SYSTEM.WHATSAPP ? 'whatsapp' : (sourceSystem === SOURCE_SYSTEM.MANUAL ? 'note' : 'source'));
  return [{
    id: 'comm-1', channel, direction: 'inbound',
    threadId: str(raw.sourceConversationId || raw.threadId), messageId: str(raw.sourceExternalId),
    from: str(contactSnapshot.email || contactSnapshot.name), receivedAt: raw.receivedAt || null,
    subject: str(raw.subject).slice(0, 500), snippet: toPlainText(raw.normalizedText || raw.bodyText || raw.body || '').slice(0, 280), summary: '',
    attachments: [],
  }];
}

// ── identity handoff to Pass 63 (source adapter provides EVIDENCE, never re-implements the resolver) ──
export function intakeIdentityEvidence(intake = {}) {
  return normalizeClientEvidence({
    organizationId: intake.organizationId,
    clientId: intake.clientId,
    sourceCustomerId: intake.sourceCustomerId,
    requesterUserId: intake.requesterUserId,
    requesterName: intake.contactSnapshot?.name,
    requesterEmail: intake.contactSnapshot?.email,
    requesterPhone: intake.contactSnapshot?.phone,
  });
}

// ── build the BookingCase draft the canonical writer (createBookingCase) consumes ────
// stage=inquiry ALWAYS (source ingestion is demand, never confirmed schedule). No canonical bookings
// are produced here — that stays the Pass-62 operator confirmation step. `resolvedClientId` comes from
// the Pass-63 resolver / operator selection.
export function buildBookingCaseDraftFromIntake(intake = {}, { resolvedClientId = '', actorNote = '' } = {}) {
  const lines = arr(intake.requestedSessionsDraft).filter((l) => !l.removed).map((l, i) => ({
    lineId: l.lineId || `line-${i + 1}`,
    date: l.date, startTime: l.startTime || '09:00',
    bookingType: l.bookingType, discipline: l.discipline, skillLevel: l.skillLevel,
    instructorsRequired: l.instructorsRequired || 1, location: l.location, notes: l.notes,
    sourceExternalSessionId: l.sourceExternalSessionId || '', // Accelerator B — carry external session lineage
  }));
  return {
    organizationId: intake.organizationId,
    seasonId: intake.seasonId,
    requesterName: intake.contactSnapshot?.name || '',
    requesterEmail: intake.contactSnapshot?.email || '',
    requesterPhone: intake.contactSnapshot?.phone || '',
    requesterUserId: intake.requesterUserId || '',
    clientId: str(resolvedClientId),
    stage: 'inquiry',                     // §18 commercial gate — NEVER confirmed on ingestion
    // source-neutral lineage onto the canonical case (§28)
    sourceType: intake.sourceType || 'manual',
    sourceReference: intake.sourceExternalId || '',
    sourceThreadId: intake.sourceConversationId || '',
    sourceMessageIds: intake.sourceExternalId ? [intake.sourceExternalId] : [],
    sourceReceivedAt: intake.receivedAt || null,
    communications: arr(intake.communications),
    requestedSessions: lines,
    notes: [intake.notes, actorNote].filter(Boolean).join('\n'),
    // provenance back-reference so a case can answer "which intake created this?"
    sourceIntakeId: intake.intakeId,
  };
}
